import React, { useEffect, useState } from "react";
import { CampaignAssignment } from "../../model/Campaign";
import { fetchCompoundActiveCampaigns } from "../../helpers/compoundCampaignsApi";
import { eur } from "../../helpers/format";

/**
 * Read-only list of the campaigns currently serving a compound (any reseller +
 * global) with their promotional prices. Admin-only (the API is ADMIN-gated).
 */
export default function ActiveCampaigns({
  compoundId,
}: {
  compoundId: string;
}) {
  const [campaigns, setCampaigns] = useState<CampaignAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCompoundActiveCampaigns(compoundId)
      .then((data) => {
        if (cancelled) return;
        setCampaigns(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            "Não foi possível carregar as campanhas.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [compoundId]);

  return (
    <section
      className="bg-white rounded-lg shadow p-6 mt-6"
      aria-labelledby="active-campaigns-heading"
    >
      <h2 id="active-campaigns-heading" className="text-xl font-semibold mb-3">
        Campanhas ativas
      </h2>
      {loading && (
        <p role="status" className="text-sm text-gray-500">
          A carregar campanhas…
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      {!loading && !error && campaigns.length === 0 && (
        <p className="text-sm text-gray-500">
          Sem campanhas ativas para este composto.
        </p>
      )}
      {!loading && campaigns.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">
              Campanhas ativas e preços promocionais
            </caption>
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th scope="col" className="py-2 pr-3">
                  Campanha
                </th>
                <th scope="col" className="py-2 pr-3">
                  Âmbito
                </th>
                <th scope="col" className="py-2 pr-3">
                  Período
                </th>
                <th scope="col" className="py-2 pr-3 text-right">
                  PVP promocional
                </th>
                <th scope="col" className="py-2 text-right">
                  Preço reseller
                </th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-b">
                  <td className="py-2 pr-3">{c.name}</td>
                  <td className="py-2 pr-3">
                    {c.is_global
                      ? "Global"
                      : `Reseller ${c.reseller_erp_ref ?? "—"}`}
                  </td>
                  <td className="py-2 pr-3">
                    {new Date(c.starts_at).toLocaleDateString("pt-PT")} —{" "}
                    {new Date(c.ends_at).toLocaleDateString("pt-PT")}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {c.promotional_pvp_with_vat != null
                      ? eur.format(c.promotional_pvp_with_vat)
                      : "—"}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {c.promotional_reseller_price != null
                      ? eur.format(c.promotional_reseller_price)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
